import {MetaTags} from '@frontend/components/MetaTags'
import {ResponsiveLayout} from '@frontend/components/ResponsiveLayout'
import {trans} from '@frontend/helpers'
import {useConfigureUi} from '@frontend/hooks/useConfigureUi'
import {PromoCards} from '@frontend/modules/layout/promo_cards/PromoCards'
import type {
    AdsListPresentation,
    FilteredItemsListPageHeaderInfo,
} from '@frontend/modules/public/ads/AdsModuleTypes'
import {RecentlyViewedAds} from '@frontend/modules/public/ads/components/list/RecentlyViewedAds'
import {useInitFilteredItemsPage} from '@frontend/modules/public/ads/hooks/useInitFilteredItemsPage'
import {LocalStorage} from '@frontend/services/LocalStorage'
import {
    mdiFormatListText,
    mdiViewGridOutline,
} from '@mdi/js'
import clsx from 'clsx'
import {useState} from 'react'
import {Collapse} from 'swayok-react-mdb-ui-kit/components/Collapse/Collapse'
import {IconButton} from 'swayok-react-mdb-ui-kit/components/Icon/IconButton'
import {Badge} from 'swayok-react-mdb-ui-kit/components/Typography/Badge'
import {AdsOrServicesBreadcrumbs} from './components/AdsOrServicesBreadcrumbs'
import {AdsCategoryFiltersPanelMobile} from './components/category_filters/AdsCategoryFiltersPanelMobile'
import {AdsCategoryFiltersPanelTabletAndDesktop} from './components/category_filters/AdsCategoryFiltersPanelTabletAndDesktop'
import {FilteredAdsList} from './components/FilteredAdsList'

// Страница со списком объявлений и дополнительной фильтрацией.
export default function AdsPage() {

    const [
        pageHeaderInfo,
        setPageHeaderInfo,
    ] = useState<FilteredItemsListPageHeaderInfo>(() => ({
        header: '',
        totalItemsCount: 0,
        categoriesPath: [],
        isLoadedOnce: false,
    }))

    const [
        adsListPresentation,
        setAdsListPresentation,
    ] = useState<AdsListPresentation>(() => LocalStorage.getAdsListPresentation('grid'))

    const {
        header,
        categoriesPath,
        totalItemsCount,
        isLoadedOnce,
    } = pageHeaderInfo

    // Настройка глобальных элементов интерфейса приложения.
    useConfigureUi(config => {
        config.topMenu.wrapInCard = true
        config.topMenu.stickToTop = true
        return config
    })

    // Инициализация страницы поиска объявлений.
    const isReady = useInitFilteredItemsPage()

    const onAdsListPresentationChange = (value: AdsListPresentation) => {
        setAdsListPresentation(value)
        LocalStorage.setAdsListPresentation(value)
    }

    if (!isReady) {
        return null
    }

    return (
        <div className="container-fluid container-xl">
            <div className="flex-container-row pb-4">
                <div className="flex-1">
                    <div>
                        {categoriesPath.length > 0 && (
                            <Collapse show={true}>
                                <AdsOrServicesBreadcrumbs
                                    path={categoriesPath}
                                    className="mb-3"
                                />
                            </Collapse>
                        )}
                        {header.length > 0 && (
                            <MetaTags
                                title={header}
                                wrapTitle
                            />
                        )}
                        {header.length > 0 && (
                            <Collapse show={true}>
                                <div
                                    className={clsx(
                                        'd-flex flex-column align-items-stretch justify-content-start',
                                        'flex-md-row align-items-md-center'
                                    )}
                                >
                                    <div className="flex-1 d-flex flex-row align-items-center justify-content-between">
                                        <div className="d-flex flex-row align-items-center justify-content-start">
                                            <h1 className="my-0 offered-items-list-page-header">
                                                {header}
                                            </h1>
                                            <Badge
                                                pill
                                                color="red"
                                                className="ms-3 fs-6 fw-400 align-middle d-inline-block"
                                            >
                                                {totalItemsCount}
                                            </Badge>
                                        </div>
                                        <AdsCategoryFiltersPanelMobile />
                                    </div>
                                    {totalItemsCount > 0 && (
                                        <div className="flex-shrink-0 mt-2 mt-md-0">
                                            <div
                                                className={clsx(
                                                    'd-flex flex-row gap-3 align-items-stretch justify-content-center',
                                                    'justify-content-md-end',
                                                    'gap-lg-2'
                                                )}
                                            >
                                                <IconButton
                                                    path={mdiViewGridOutline}
                                                    size={20}
                                                    color={adsListPresentation === 'grid' ? 'primary' : 'muted'}
                                                    tooltip={trans().ads.list.presentation.grid}
                                                    onClick={() => onAdsListPresentationChange('grid')}
                                                />
                                                <IconButton
                                                    path={mdiFormatListText}
                                                    size={20}
                                                    color={adsListPresentation === 'list' ? 'primary' : 'muted'}
                                                    tooltip={trans().ads.list.presentation.list}
                                                    onClick={() => onAdsListPresentationChange('list')}
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </Collapse>
                        )}
                    </div>
                    <div className="flex-container-row mt-3 mt-sm-4">
                        {isLoadedOnce && (
                            <AdsCategoryFiltersPanelTabletAndDesktop />
                        )}
                        <FilteredAdsList
                            key={adsListPresentation}
                            setPageHeaderInfo={setPageHeaderInfo}
                            adsPresentation={adsListPresentation}
                        />
                    </div>
                    <ResponsiveLayout
                        desktop={null}
                        wideTablet={
                            <>
                                <RecentlyViewedAds
                                    standalone
                                    className="pt-3"
                                />
                                <div className="promo-cards-block-standalone">
                                    <div className="mt-4 fw-500 fs-5">
                                        {trans().ads.list.features}:
                                    </div>
                                    <PromoCards
                                        className="mt-3 grid-columns-md-2"
                                        utmSource="search_page"
                                    />
                                </div>
                            </>
                        }
                        mobile={
                            <PromoCards
                                className="mt-4 promo-cards-block-standalone"
                                utmSource="search_page"
                            />
                        }
                    />
                </div>
            </div>
        </div>
    )
}
